import { Column } from 'typeorm';
import type { InstantlyWebhookState } from '../../constants/instantly';
import { Entity } from '../../decorators';
import { BaseEntity } from './base.entity';

/**
 * PD-17608 — the tenant's connection to its own Instantly workspace: what the
 * key belongs to (workspace and plan, re-read by every full sync), the
 * webhooks Workd registered there, and how the last sync went. At most one
 * live row per tenant; disconnect soft-removes it.
 *
 * The API key and the webhook secret are NOT here: both live encrypted in the
 * instance `_secret_store` (the Motive pattern), and connection state is the
 * key's existence there.
 *
 * Not auditable: the hourly full sync rewrites the plan counts and sync date,
 * which would otherwise add a meaningless `_audit` row every hour. Connect and
 * disconnect are still recorded in `_request`.
 */
@Entity('instantly_connection', {}, { auditable: false })
export class InstantlyConnectionEntity extends BaseEntity {
	/** Instantly's workspace (organization) id the key belongs to. */
	@Column('varchar', { name: 'workspaceId', length: 64 })
	public workspaceId!: string;

	@Column('varchar', { name: 'workspaceName', length: 255, nullable: true })
	public workspaceName!: string | null;

	/** The outreach subscription's plan name (e.g. "Light Speed"). */
	@Column('varchar', { name: 'planName', length: 120, nullable: true })
	public planName!: string | null;

	/** People the plan may hold at once (the people-slot cap). */
	@Column('int', { name: 'leadLimit', nullable: true })
	public leadLimit!: number | null;

	/** People currently held, as of the last sync. */
	@Column('int', { name: 'leadCount', nullable: true })
	public leadCount!: number | null;

	@Column('varchar', { name: 'webhookState', length: 16, default: () => "'Not Checked'" })
	public webhookState!: InstantlyWebhookState;

	/** Instantly ids of the webhooks Workd registered, so disconnect removes exactly those. */
	@Column('json', { name: 'webhookIds', nullable: true })
	public webhookIds!: Array<string> | null;

	@Column('datetime', { name: 'lastSyncDate', nullable: true })
	public lastSyncDate!: Date | null;

	/** Why the last sync failed; null when it succeeded. */
	@Column('varchar', { name: 'lastSyncError', length: 500, nullable: true })
	public lastSyncError!: string | null;

	@Column('datetime', { name: 'connectedDate' })
	public connectedDate!: Date;

	/** Tenant user (user._id) who connected the workspace. */
	@Column('int', { name: 'fk_user__id_connectedBy', unsigned: true, nullable: true })
	public fkUserIdConnectedBy!: number | null;

	@Column('varchar', { name: 'connectedByName', length: 255, nullable: true })
	public connectedByName!: string | null;

	/**
	 * PD-17614 — the start of the last catch-up that finished cleanly. The next
	 * catch-up re-reads from Instantly everything since then (sent emails,
	 * replies, lead statuses), so Workd never relies on webhooks alone.
	 */
	@Column('datetime', { name: 'lastCatchUpDate', nullable: true })
	public lastCatchUpDate!: Date | null;

	@Column('varchar', { name: 'lastCatchUpError', length: 500, nullable: true })
	public lastCatchUpError!: string | null;

	/** PD-17614 — the Instantly lead list ended runs are moved to (created on first use). */
	@Column('varchar', { name: 'endedLeadListId', length: 64, nullable: true })
	public endedLeadListId!: string | null;
}
